import { Injectable, Injector, inject } from '@angular/core';

import { Subject } from 'rxjs';

import { HA_APP_KEY } from '../client-config/client-config.model';
import { CLIENT_CONFIG_TOKEN } from '../client-config/client-config.token';
import { ProductInfo, ProductsConfig } from './products.client-config';

/**
 * Metadata about the single domain app products provided by {@link ProductService}.
 *
 * @stable
 */
export class ProductMetadata {
    private readonly _name: string;
    private _injector: Injector;
    private readonly _apiBaseUrl: string;
    private readonly _enabled: boolean;
    private readonly _enabledProductApi: boolean;
    private _isRegistered: boolean;

    get name(): string {
        return this._name;
    }

    get apiBaseUrl(): string {
        return this._apiBaseUrl;
    }

    get isEnabled(): boolean {
        return this._enabled;
    }

    get isEnabledProductApi(): boolean {
        return this._enabledProductApi;
    }

    get isRegistered(): boolean {
        return this._isRegistered;
    }

    get isHost(): boolean {
        return this._name === 'host';
    }

    get injector(): Injector {
        this.requireRegistered();

        return this._injector;
    }

    constructor(
        readonly product: string,
        readonly productInfo: ProductInfo,
    ) {
        this._enabled = productInfo.enabled;
        this._enabledProductApi = productInfo.enabledProductApi;
        this._apiBaseUrl = productInfo.apiBaseUrl;
        this._name = product;
    }

    /** @internal */
    onRegister(options: { injector: Injector }) {
        this._injector = options.injector;
        this._isRegistered = true;
    }

    private requireRegistered() {
        if (!this.isRegistered) {
            throw new Error(`Product ${this.name} must be loaded to access the requested property.`);
        }
    }
}

/**
 * @whatItDoes Provides a way for a single domain app to register products and read metadata about them.
 *
 * @howToUse
 * ```
 * @NgModule()
 * export class SportsModule { // Module to be lazy loaded by the single domain app
 *     constructor(productService: ProductService, injector: Injector) {
 *         productService.register('sports', injector);
 *     }
 * }
 * ```
 *
 * The metadata are combine from [dynacon](https://admin.dynacon.prod.env.works/services/121971/features/126782) and the calls to `register`.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class ProductService {
    private readonly _clientConfig = inject(CLIENT_CONFIG_TOKEN);
    private readonly productConfig = inject(ProductsConfig);

    private static readonly DEFAULT_PRODUCT = 'host';
    private static readonly HOST_METADATA = new ProductMetadata('host', { apiBaseUrl: '', enabled: true, enabledProductApi: true });
    private activeProduct: string = ProductService.DEFAULT_PRODUCT;
    private readonly metadata = new Map<string, ProductMetadata>();
    readonly productChanged = new Subject<ProductMetadata>();

    get current(): ProductMetadata {
        return this.getMetadata(this.activeProduct);
    }

    get isSingleDomainApp(): boolean {
        return HA_APP_KEY in this._clientConfig;
    }

    get defaultProduct(): string | undefined {
        if (!this.isSingleDomainApp) {
            return undefined;
        }
        const happ = this._clientConfig[HA_APP_KEY];
        return happ?.defaultProduct;
    }

    get productApiUrls(): { [key: string]: string } {
        if (!this.isSingleDomainApp) {
            return {};
        }

        const urls = this._clientConfig[HA_APP_KEY]['productApiUrls'];
        if (!urls) {
            throw new Error(`App is running in single domain mode, but productApiUrls is not configured.`);
        }

        return urls;
    }

    getMetadata(product: string): ProductMetadata {
        if (this.isSingleDomainApp) {
            if (product === ProductService.DEFAULT_PRODUCT) {
                return ProductService.HOST_METADATA;
            }

            if (!this.metadata.has(product)) {
                const keys = Object.keys(this.productApiUrls);
                if (!keys.includes(product)) {
                    throw new Error(`Product ${product} is not configured in productApiUrls. Configured ones are: ${keys}.`);
                }

                this.metadata.set(
                    product,
                    new ProductMetadata(product, { apiBaseUrl: this.productApiUrls[product]!, enabled: true, enabledProductApi: true }),
                );
            }

            return this.metadata.get(product)!;
        }

        if (!this.productConfig[product]) {
            throw new Error(`Product ${product} is not configured.`);
        }

        if (!this.metadata.has(product)) {
            this.metadata.set(product, new ProductMetadata(product, this.productConfig[product]));
        }

        return this.metadata.get(product)!;
    }

    register(product: string, injector: Injector) {
        const metadata = this.getMetadata(product);
        metadata.onRegister({ injector });
    }

    setActive(product: string) {
        if (this.activeProduct === product) {
            return;
        }

        const metadata = this.getMetadata(product);
        if (!metadata.isRegistered) {
            throw new Error(`Cannot set active product ${product} because it was not registered.`);
        }

        this.activeProduct = product;
        this.productChanged.next(this.current);
    }
}
